import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ClipboardList, CheckCircle2, AlertCircle, ChevronRight, ChevronDown, CheckSquare } from 'lucide-react';
import { theme, cardStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  SOP + STAFF TASKS WIDGET — shows pending register tasks + staff
//  tasks on Dashboard. Click task → one-click complete.
// ═══════════════════════════════════════════════════════════════════

const KIND_ICON = {
  'opg': '🩻', 'instrument-cleaning': '🧽', 'autoclave': '♨️',
  'bmw': '🗑️', 'nsi': '⚠️', 'er-box': '🚑', 'mrd-checklist': '📁',
  'calibration': '🎯', 'cleaning': '🧼', 'fire-drill': '🔥',
  'stock-register': '💊', 'training': '🎓', 'patient-fall': '🩹',
  'infection-control': '🦠', 'pms': '🔧',
  'daily-rounds': '🚶', 'facility-inspection': '🔍', 'toilet-cleaning': '🚿',
  'linen': '🧺', 'patient-record-request': '🗂️', 'fumigation': '💨',
  'patient-referral': '↗️', 'medication-error': '💊', 'housekeeping-roster': '🧹',
};

const SOPTasksWidget = ({ API_BASE, currentUser, onNavigate }) => {
  const [tasks, setTasks] = useState([]);
  const [staffTasks, setStaffTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    try {
      const [reg, st] = await Promise.all([
        axios.get(`${API_BASE}/registers/pending-today`),
        axios.get(`${API_BASE}/staff-tasks`, { params: { status: 'open', limit: 10 } }),
      ]);
      setTasks(reg.data || []);
      setStaffTasks(st.data || []);
    } catch (_) {} finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  const markComplete = async (id) => {
    try {
      await axios.post(`${API_BASE}/registers/${id}/complete`, { completedBy: currentUser?.fullName || 'staff' });
      load();
    } catch (_) {}
  };

  const markStaffTaskDone = async (id) => {
    try {
      await axios.post(`${API_BASE}/staff-tasks/${id}/complete`, { completedBy: currentUser?.fullName || 'staff' });
      load();
    } catch (_) {}
  };

  const nilToday = async () => {
    if (!window.confirm('Aaj ke event-style registers ko "No event today" mark karein?\n(NSI, fall, med error, referral, fumigation, infection control, fire-drill)')) return;
    try {
      const r = await axios.post(`${API_BASE}/registers/nil-today`, { completedBy: currentUser?.fullName || 'staff' });
      alert(r.data.created === 0
        ? `Sab nil-registers ke liye aaj ki entry already hai (${r.data.skipped} skipped)`
        : `✓ ${r.data.created} nil entries created${r.data.skipped ? ` · ${r.data.skipped} already done` : ''}`);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  if (loading) return null;

  const overdueCount = tasks.filter(t => t.status === 'overdue').length;
  const totalPending = tasks.length + staffTasks.length;
  const allClear = totalPending === 0;

  return (
    <div style={{
      ...cardStyle, padding: 12, marginBottom: 14,
      borderLeft: `4px solid ${allClear ? theme.success : overdueCount > 0 ? theme.danger : theme.warning}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 34, height: 34, borderRadius: 8, background: theme.warning + '22', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ClipboardList size={18} color={theme.warning} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h3 style={{ margin: 0, fontSize: 14.5, color: theme.navy }}>Pending SOP Tasks</h3>
            {allClear ? (
              <span style={{ fontSize: 11, fontWeight: 700, color: theme.success, padding: '1px 8px', background: theme.success + '18', borderRadius: 999 }}>
                ✓ All clear
              </span>
            ) : (
              <span style={{ fontSize: 11, fontWeight: 700, color: theme.warning, padding: '1px 8px', background: theme.warning + '18', borderRadius: 999 }}>
                {tasks.length} SOP{staffTasks.length > 0 ? ` · ${staffTasks.length} staff` : ''}
                {overdueCount > 0 && <span style={{ color: theme.danger, marginLeft: 6 }}>· {overdueCount} overdue</span>}
              </span>
            )}
          </div>
          <p style={{ margin: '2px 0 0', fontSize: 11.5, color: theme.textMuted }}>NABH compliance checks</p>
        </div>
        <button onClick={nilToday}
          title="One-click: mark NSI, fall, medication error, fumigation, etc. as 'No event today'"
          style={{
            padding: '4px 10px', borderRadius: 6, border: `1px solid ${theme.success}`,
            background: theme.success + '14', color: theme.success,
            cursor: 'pointer', fontSize: 11.5, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
          <CheckCircle2 size={12} /> No Event Today
        </button>
        {onNavigate && (
          <button onClick={() => onNavigate('registers')} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: theme.primary, fontWeight: 700, fontSize: 12.5,
            display: 'flex', alignItems: 'center', gap: 3,
          }}>
            View all <ChevronRight size={14} />
          </button>
        )}
        <button
          onClick={() => setExpanded(e => !e)}
          title={expanded ? 'Collapse' : 'Expand task list'}
          disabled={allClear}
          style={{
            background: 'none', border: `1px solid ${theme.border}`,
            borderRadius: 6, padding: '4px 6px',
            cursor: allClear ? 'default' : 'pointer',
            opacity: allClear ? 0.4 : 1,
            display: 'inline-flex', alignItems: 'center', color: theme.textMuted,
          }}
        >
          <ChevronDown size={14} style={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}/>
        </button>
      </div>

      {expanded && (tasks.length === 0 && staffTasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '18px 0', color: theme.success }}>
          <CheckCircle2 size={28} style={{ opacity: 0.6 }} />
          <p style={{ margin: '6px 0 0', fontSize: 13, fontWeight: 600 }}>Sab compliance clear hai 🎉</p>
        </div>
      ) : (
        <div style={{ maxHeight: 260, overflowY: 'auto' }}>
          {tasks.slice(0, 6).map(t => (
            <div key={t._id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 10px', marginBottom: 4, borderRadius: 7,
              background: t.status === 'overdue' ? '#fef2f2' : '#fffbeb',
              borderLeft: `3px solid ${t.status === 'overdue' ? theme.danger : theme.warning}`,
            }}>
              <span style={{ fontSize: 18 }}>{KIND_ICON[t.kind] || '📋'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: theme.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {t.title}
                </div>
                <div style={{ fontSize: 11, color: theme.textMuted }}>
                  {t.patientName ? `${t.patientName} · ` : ''}{new Date(t.date).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              <button onClick={() => markComplete(t._id)} style={{
                padding: '4px 10px', borderRadius: 5, background: theme.success,
                color: 'white', border: 'none', cursor: 'pointer',
                fontSize: 11.5, fontWeight: 700,
              }}>
                ✓ Done
              </button>
            </div>
          ))}
          {tasks.length > 6 && (
            <p style={{ fontSize: 11.5, color: theme.textMuted, textAlign: 'center', marginTop: 6 }}>
              +{tasks.length - 6} more SOP pending
            </p>
          )}

          {/* STAFF TASKS section */}
          {staffTasks.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 14, marginBottom: 6, paddingTop: 10, borderTop: `1px solid ${theme.borderLight}` }}>
                <CheckSquare size={13} color={theme.primary} />
                <span style={{ fontSize: 11, fontWeight: 700, color: theme.primary, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Staff Tasks
                </span>
                {onNavigate && (
                  <button onClick={() => onNavigate('staff-tasks')} style={{
                    marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                    color: theme.primary, fontWeight: 700, fontSize: 11,
                  }}>View all →</button>
                )}
              </div>
              {staffTasks.slice(0, 4).map(t => (
                <div key={t._id} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 10px', marginBottom: 4, borderRadius: 7,
                  background: t.priority === 'urgent' ? '#fef2f2' : '#f0f9ff',
                  borderLeft: `3px solid ${t.priority === 'urgent' ? theme.danger : theme.info}`,
                }}>
                  <span style={{ fontSize: 16 }}>
                    {t.priority === 'urgent' ? '🔥' : t.priority === 'high' ? '⬆️' : '📌'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.navy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.title}
                    </div>
                    <div style={{ fontSize: 11, color: theme.textMuted }}>
                      {t.assignedToName || (t.role ? `Any ${t.role}` : 'Unassigned')}
                    </div>
                  </div>
                  <button onClick={() => markStaffTaskDone(t._id)} style={{
                    padding: '4px 10px', borderRadius: 5, background: theme.success,
                    color: 'white', border: 'none', cursor: 'pointer',
                    fontSize: 11.5, fontWeight: 700,
                  }}>
                    ✓ Done
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

export default SOPTasksWidget;
